import React, { useState, useEffect } from 'react';
import {
  getReports,
  markRead,
  markUnread,
  deleteReport,
} from '../services/reportService';
import './Reports.css';

interface ReportRide {
  id: number;
  origin: string | null;
  destination: string | null;
  departureDate: string | null;
}

interface ReportReporter {
  /** null for a sender without an account (public contact form). */
  id: number | null;
  fullName: string | null;
  email: string | null;
  phone?: string | null;
  guest?: boolean;
}

interface ReportItem {
  id: number;
  subject: string;
  body: string;
  readAt: string | null;
  createdAt: string;
  reporter: ReportReporter | null;
  ride: ReportRide | null;
}

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '';
  return new Date(value).toLocaleDateString();
};

const Reports = () => {
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'unread' | 'all'>('unread');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getReports({ status: statusFilter, page, limit: 20 });
      setReports(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (report: ReportItem) => {
    const isExpanding = expandedId !== report.id;
    setExpandedId(isExpanding ? report.id : null);

    if (isExpanding && !report.readAt) {
      try {
        await markRead(report.id);
        if (statusFilter === 'unread') {
          setReports((prev) => prev.filter((r) => r.id !== report.id));
        } else {
          setReports((prev) =>
            prev.map((r) => (r.id === report.id ? { ...r, readAt: new Date().toISOString() } : r))
          );
        }
      } catch (err: any) {
        console.error('Failed to mark report as read', err);
      }
    }
  };

  const handleToggleReadStatus = async (e: React.MouseEvent, report: ReportItem) => {
    e.stopPropagation();
    try {
      if (report.readAt) {
        await markUnread(report.id);
        setReports((prev) =>
          prev.map((r) => (r.id === report.id ? { ...r, readAt: null } : r))
        );
      } else {
        await markRead(report.id);
        if (statusFilter === 'unread') {
          setReports((prev) => prev.filter((r) => r.id !== report.id));
        } else {
          setReports((prev) =>
            prev.map((r) => (r.id === report.id ? { ...r, readAt: new Date().toISOString() } : r))
          );
        }
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update report');
    }
  };

  const handleDelete = async (e: React.MouseEvent, report: ReportItem) => {
    e.stopPropagation();
    if (!window.confirm('Delete this report? This cannot be undone from the UI.')) return;
    try {
      await deleteReport(report.id);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete report');
    }
  };

  return (
    <div className="reports-page">
      <h1>Inbox</h1>

      <div className="filters-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'unread' | 'all');
              setPage(1);
              setExpandedId(null);
            }}
          >
            <option value="unread">Unread</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading reports...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : reports.length === 0 ? (
        <div className="no-data">No reports found</div>
      ) : (
        <>
          <div className="reports-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Reporter</th>
                  <th>Subject</th>
                  <th>Ride</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => {
                  const isUnread = !report.readAt;
                  const isExpanded = expandedId === report.id;
                  const reporterName = report.reporter?.fullName || '(deleted user)';
                  const reporterEmail = report.reporter?.email || '';
                  const rideText = report.ride
                    ? `${report.ride.origin || ''} → ${report.ride.destination || ''}${report.ride.departureDate ? `, ${formatDate(report.ride.departureDate)}` : ''}`
                    : '-';

                  return (
                    <React.Fragment key={report.id}>
                      <tr
                        className={`report-row ${isUnread ? 'unread' : ''} ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => handleRowClick(report)}
                      >
                        <td>{formatDateTime(report.createdAt)}</td>
                        <td>
                          <div className="reporter-name">
                            {reporterName}
                            {report.reporter?.guest && <span className="reporter-email"> (no account)</span>}
                          </div>
                          {reporterEmail && <div className="reporter-email">{reporterEmail}</div>}
                          {report.reporter?.phone && <div className="reporter-email">{report.reporter.phone}</div>}
                        </td>
                        <td>
                          {isUnread && <span className="unread-dot" aria-hidden="true" />}
                          <span className={isUnread ? 'subject-unread' : ''}>{report.subject}</span>
                        </td>
                        <td>{rideText}</td>
                        <td>
                          <button
                            className="btn btn-sm"
                            onClick={(e) => handleToggleReadStatus(e, report)}
                          >
                            {report.readAt ? 'Mark unread' : 'Mark read'}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={(e) => handleDelete(e, report)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="report-body-row">
                          <td colSpan={5}>
                            <div className="report-body">{report.body}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setPage(page - 1)} disabled={page === 1}>
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
